import {
    JobberWorkerTypeDTO,
    ServicedCity,
    PhoneNumber,
    JobberStatus,
    JobberWorkerTypeAgencyPrivateViewDTO,
    LaborSalesTaxRuleDTO,
    OrgHourlyRateMarkupType,
    PreferredCommunicationMethod,
    WorkyUserDetails,
    UserDetails,
    AddressDto,
    CorpJobberDetails,
    OrgLocationMapDetails,
    OrgDetailsAgencyDisplayStatus,
    EmailAddress,
    OrgJobberMatchingType,
    WorkyUserDetailsZip,
    BillingTimeInterval,
} from "../../common/CommonInterfaces";
import { OrgLocationDisplayStatus } from "../../common/OrgLocationDisplayStatus";
import { AgencyAdminShiftClockDetailsDTO } from "../common-shift-components/common-shift-interfaces/CommonShiftInterfaces";
import { FormSubElementTypes } from "../../common/form-shared-service/FormSharedInterfaces";
import {
    ConditionOperator,
    OnboardingQuestionType,
} from "../../jobberViews/objects/JobberOnboardingTemplateInterfaces";
import { PayStubJobberAckDTO } from "../../common/worky-pay/WorkyPayCommonInterfaces";
import { ShiftPaymentJobberDisputeDTO } from "../../jobberViews/jobber-data/JobberInterfaces";
import {
    WorkyPayCorpConfigClient,
    WorkJurisdictionIdentifier,
    ShiftAssignmentConfigRules,
    ShiftAssignmentConfigRuleType,
    ShiftAssignmentConfigRuleApplicationType,
    PremiumPayApplicConfigType,
    OrgClientHourlyDeductionApplicType,
    WorkerPayShiftBreakDeductionMode,
} from "../../orgViews/orgConstants";
import { JobberContractorDetailsDTO } from "../../jobberViews/jobber-data/JobberCommonInterfaces";

interface AgencyUser {
  userDetails: UserDetails;
  agencyName: string;
}

/**
 * This is on purpose a DTO only object,
 * Source of truth for jobber pre training to facility is saved on the facility
 * obj directly on the agency obj, t.f we do not need to persist to DB
 */
interface PreloadJobber {
  readonly jobberDetails: WorkyUserDetailsZip;
  readonly jobberWorkerTypes: readonly JobberWorkerTypeDTO[];
  readonly jobberEmploymentDeductionsConfig?: readonly JobberEmploymentDeductionsConfigDTO[];
  readonly orgLocationConfigs?: readonly OrgJobberLocationConfigDTO[] | null;
}


/** ISO 3166-1 alpha-2 primary country from agency corpBasicDetails (e.g. CA, US). */
interface CorpConfigBasicDetailsClient {
  readonly primaryCountryCode: string;
  readonly primaryCurrencyCode?: string;
  readonly primaryShiftTimezone?: string;
  readonly primaryBillingTimezone?: string | null;
  readonly primaryPayTimezone?: string | null;
  readonly primaryWorkJurisdictionIdentifier?: string;
  readonly primaryTimeDisplayFormat?: string | null;
}

interface AgencyObjClient {
  primaryWorkyCorpId: string;
  iconImageS3Key: string;
  corpJobberDetails: CorpJobberDetails;
  addressDto: AddressDto;
  agencyShiftDetailsMapForEachOrg: OrgLocationMapDetails;
  disabledAgencyShiftDetailsMapForEachOrg?: Record<string, OrgLocationMapDetails>;
  agencyUsers: AgencyUser[];
  jobberDetails: Record<string, PreloadJobber>;
  jobberWorkerTypes : JobberWorkerTypeAgencyPrivateViewDTO[];
  workyPayCorpConfig: Map<string, WorkyPayCorpConfigClient>;
  shiftAssignmentConfigRules?: ShiftAssignmentConfigRules;
  shiftTemplates?: readonly ShiftTemplateDTO[];
  /** Locale / residency config; when absent, treat primary country as CA. */
  corpBasicDetails?: CorpConfigBasicDetailsClient | null;
}

interface JobberHourlyRateToBeUsedForPayrollCalcDetailDTO {
  readonly jobberHourlyRateToBeUsedForPayrollCalc: number;
  readonly totalHoursForPayroll: number;
  readonly detailNoteForPayStub?: string;
  readonly timeDeductionOverrideSec?: number;
  readonly totalHourlyCost?: number;
  readonly totalPayForShift?: number;
  readonly startDateTimeEpochSec?: number;
  readonly endDateTimeEpochSec?: number;
}

interface JobberHourlyRateToBeUsedForPayrollCalcDTO {
  readonly hourlyRatesDetails?: readonly JobberHourlyRateToBeUsedForPayrollCalcDetailDTO[];
}

interface ShiftDetailsJobberPayStubDTO {
  shiftId: string;
  shiftStartDateTimeEpochSec: number;
  shiftEndDateTimeEpochSec: number;
  jobberHourlyRate: number;
  totalHoursWorked: number;
  totalPayForShift?: number | null;
  deductionAmountAtShiftLevel: Map<string, number>;
  workerTypeUniqueIdentifier?: string;
  workerTypeName?: string;
  orgName?: string;
  orgDisplayName?: string;
  orgLocationName?: string;
  orgFacilityDisplayName?: string;
  clockInOutDetails?: AgencyAdminShiftClockDetailsDTO;
  jobberHourlyRateToBeUsedForPayrollCalc?: JobberHourlyRateToBeUsedForPayrollCalcDTO;
}
interface WorkyPayCorpOwnerReview {
  corpOwnerReviewDetail: WorkyPayCorpOwnerReviewDetail
}


/**
 * Defines whether or not the paystub details are actual retrieved from WorkyPayStub DB record
 * or created in memory by the application code due to the fact that the pay cycle is in progress
 */

enum WorkyPayStubRecordSourceType {
    
    PENDING = "PENDING",
    
    CREATED = "CREATED"
}

interface CurrentAgencyPayStubsJobberLevelDTO {
  payStubEndDateTimeEpochSecDisplayOnly: number;
  jobberDetails: WorkyUserDetails;
  totalHoursWorked: number;
  grossEarning: number;
  jobberObligationsDeductionMap: Map<string, number>;
  corpObligationsDeductionMap: Map<string, number>;
  shiftDetailsJobberPayStubViewMap: Map<string, ShiftDetailsJobberPayStubDTO>;
  payStubJobberAck?: PayStubJobberAckDTO | null;
  workyPayCorpOwnerReview?: WorkyPayCorpOwnerReview | null;
  sourceType?: WorkyPayStubRecordSourceType;
  
}
enum WorkyPayPayCycleStatus {
  YET_TO_BE_SAVED_TO_DB = "YET_TO_BE_SAVED_TO_DB",
  OPEN = "OPEN",
  CLOSED = "CLOSED",
}

enum PayCorpOwningReviewStatus {
  /**
   * The agency owner has not yet reviewed or changed status
   */
  PENDING = "PENDING",
  /**
   * The agency owner has approved the paystub
   */
  APPROVED = "APPROVED",
  /**
   * The agency owner has disputed the paystub
   */
  DISPUTED = "DISPUTED",
  /**
   * The agency owner has denied the paystub
   */
  DENIED = "DENIED",
}
interface CurrentAgencyPayStubsRespDTO {
  payStubCycleStartDateTimeEpochSec: number;
  payStubCycleEndDateTimeEpochSec: number;
  payStubCycleEndDateTimeEpochSecDisplay: number;
  workJurisdictionIdentifier?: WorkJurisdictionIdentifier;
  currentPayStubs: CurrentAgencyPayStubsJobberLevelDTO[];
  totalCorpObligations: Map<string, number>;
  status: WorkyPayPayCycleStatus;
}

interface WorkyBillPaymentBreakdownDTO {
  workyHourlyMarginRate: number;
  jobberHourlyRate: number;
  orgHourlyRate: number;
  totalTimeWorked: number;
  totalHourlyCost: number;
  taxRate?: number | null;
  taxAmount: number;
  wsipAmount: number;
  otherExpenses?: number | null;
  totalCost: number;
  originalTotalCost: number;
  currentOutstandingTotalCost: number;
}

/**
 * Detail line for jobber hourly rate spans used in agency-org receiving corp billing.
 */
interface JobberHourlyRateToBeUsedForReceivingCorpBillingDetailDTO {
  readonly jobberHourlyRateToBeUsedForReceivingCorpBilling: number;
  readonly startDateTimeEpochSec: number;
  readonly endDateTimeEpochSec: number;
  /** Not just end − start; can include lunch break deduction. */
  readonly totalHoursForBilling: number;
  readonly totalHourlyCost: number;
  readonly taxAmount?: number | null;
  /** Short note for org invoice PDF. */
  readonly detailNoteForOrgInvoice?: string | null;
  /**
   * Agency override: seconds added back into shift span treated as deduction from billed time (duration, not epoch). Null when none.
   */
  readonly hourlyDeductionOverrideAmount?: number | null;
}

interface JobberHourlyRateToBeUsedForReceivingCorpBillingDTO {
  readonly hourlyRatesDetails?: readonly JobberHourlyRateToBeUsedForReceivingCorpBillingDetailDTO[] | null;
}

/**
 * Agency-org invoice billing breakdown for shift payment (aligned with server billing DTO for finalized cycle summaries).
 */
interface WorkyBillBreakDownAgencyOrgBillingSummaryView {
  readonly workyHourlyMarginRate: number;
  readonly jobberHourlyRate: number;
  readonly orgHourlyRate: number;
  readonly totalTimeWorked: number;
  readonly totalHourlyCost: number;
  readonly taxRate?: number | null;
  readonly taxAmount: number;
  readonly otherExpenses?: number | null;
  readonly totalCost: number;
  readonly originalTotalCost: number;
  readonly currentOutstandingTotalCost: number;
  readonly jobberHourlyRateToBeUsedForReceivingCorpBillingExpanded?: JobberHourlyRateToBeUsedForReceivingCorpBillingDTO | null;
}

/**
 * Actual agency-org invoice billing details for the shift.
 * Populated only when a finalized AgencyOrgBillingCycleSummary exists for the original shift payment.
 */
interface AgencyOrgBillingShiftBillingDetailsDataDTO {
  readonly workyBillPaymentBreakdown: WorkyBillBreakDownAgencyOrgBillingSummaryView;
}

interface ShiftBillingDetailsDataDTO {
  shiftId: string;
  jobberDetails: UserDetails;
  startDateTimeEpochSec: number;
  endDateTimeEpochSec: number;
  workyBillPaymentBreakdown: WorkyBillPaymentBreakdownDTO;
  /**
   * Facility work jurisdiction for this shift (drives wall-clock timezone for invoice display).
   * Absent on older persisted invoice rows until regenerated.
   */
  workJurisdictionIdentifier?: WorkJurisdictionIdentifier;
  shiftLocationName?: string | null;
}

interface ShiftBillingDetailsDTO {
    shiftBillingDetailsData: Record<string, ShiftBillingDetailsDataDTO>;
}


enum TemplateStatusEnum {
    ACTIVATED = "ACTIVATED",
    DEACTIVATED = "DEACTIVATED"
}

interface AgencyOrgBillingParallelInvoiceFileDetailDTO {
    readonly recordId?: string;
    readonly parallelAgencyOrgBillingCycleSummaryConfigId: string;
    readonly invoiceConfigTypeName: string;
    readonly fileCreationDateTimeEpochSec?: number;
    readonly fileName?: string | null;
    readonly externalStorageFileKey?: string | null;
}



interface AgencyOrgBillingCycleSummaryDTO {
    billingCyclePartitionKey: string;
    agencyName: string;
    orgName: string;
    orgDisplayName: string;
    totalShiftCount: number;
    totalWorkedHoursCount: number;
    originalTotalAmountOwed: number;
    currentOutstandingTotalAmountOwed: number;
    shiftBillingDetailsMap: Record<string, ShiftBillingDetailsDTO>;
    shiftPaymentOrgStatus: ShiftPaymentOrgStatus;
    billingCycleStartTimeEpochSec: number;
    orgBillingFileMostRecentCreationDateTimeEpochSec: number;
    billingCycleEndTimeEpochSec: number;
    /** Epoch (seconds) used for month header display; falls back to billingCycleEndTimeEpochSec when absent. */
    billingCycleEndTimeEpochSecDisplay?: number;
    /** Persisted invoice due date (epoch seconds), when set on the org bill. */
    billDueDateEpochSec?: number | null;
    /** Stamped primary invoice number after first successful PDF; absent until generated. */
    invoiceNumber?: string | null;
    /** Primary invoice issue date (epoch seconds), frozen with invoiceNumber. */
    invoiceIssueDateEpochSec?: number | null;
    parallelInvoiceFileDetails?: readonly AgencyOrgBillingParallelInvoiceFileDetailDTO[] | null;
}

interface ListAgencyOrgBillingCycleSummaryHeadersRespDTO {
  invoiceHeaders: AgencyOrgBillingCycleSummaryDTO[];
  nextPageToken?: string | null;
}

interface SearchAgencyOrgBillsByInvoiceNumberRespDTO {
  readonly invoices: readonly AgencyOrgBillingCycleSummaryDTO[];
}

enum CheckInOutType {
  TIME_CLOCK_CODE = "TIME_CLOCK_CODE",
  GEO_LOCATION = "GEO_LOCATION"
}

interface CheckInOutDataDTO{
  checkInOutType?: CheckInOutType;
  isCheckInOutInvalid?: boolean;
  checkInOutDateTimeEpochSec?: number;
}

interface ShiftDetailsClockInOutDTO{
  plannedShiftStartDateTimeEpochSec: number;
  plannedShiftEndDateTimeEpochSec: number;
}

interface JobberCheckInOutDataDTO {
  shiftStartDate: string;
  orgName: string;
  shiftId: string;
  jobberUserDetails: WorkyUserDetails;
  shiftDetailsClockInOutDTO?: ShiftDetailsClockInOutDTO;
  /**
   * if null it means the jobber has not checked in yet
   */
  checkInData?: CheckInOutDataDTO;
  checkOutData?: CheckInOutDataDTO;
}

interface TimeClockOrgDetails {
  iconImageS3Key: string;
  corpJobberDetails: CorpJobberDetails;
}

interface TimeClockByOrgByDateByLocationDTO {
  orgName: string;
  shiftStartDateTime: string;
  jobberCheckInDataByLocation: JobberCheckInOutDataDTO[];
}

interface ShiftForOrgOnThisDay {
  shiftStartDate: string;
  orgName: string;
  timeClockByOrgByDateByLocations: TimeClockByOrgByDateByLocationDTO[];
}

interface TimeClockOrgDataRespDTO {
  shiftStartDate: string;
  shiftsForOrgOnThisDay: ShiftForOrgOnThisDay[];
}

interface TimeClockDataRespDTO{
  timeClockOrgDetails: TimeClockOrgDetails;
  timeClockOrgDataRespDTO: TimeClockOrgDataRespDTO[]
}

enum PDFRecordRequestType{
  GENERATE = 'GENERATE',
  DOWNLOAD = 'DOWNLOAD'
}

interface DownloadOrgBillReqDTO {
    orgName: string;
    billingCycleStartTimeEpochSec: number;
    billingCycleEndTimeEpochSec: number;
    recordRequestType: PDFRecordRequestType;
    /** When present, targets the parallel invoice config for generate/download. */
    parallelAgencyOrgBillingCycleSummaryConfigId?: string | null;
    /** Optional invoice template id to be used for PDF generation. */
    templateId?: string | null;
}


interface AgencyOrgBillRowObj {
  id: string;
  orgName: string;
  status: string;
  outstandingCost: number;
  totalCost: number;
  shiftBillingDetailsMap: Record<string, ShiftBillingDetailsDTO>;
  billingCycleStartTimeEpochSec: number;
  billingCycleEndTimeEpochSec: number;
  billingCycleEndTimeEpochSecDisplay: number;
}

enum DownloadOrgBillAgencyResStatus {
  /**
   * This means a SQS message has been sent to generate this pdf
   */
  REQUEST_RECEIVED_PDF_GENERATION_IN_PROGRESS = "REQUEST_RECEIVED_PDF_GENERATION_IN_PROGRESS",
  UNABLE_TO_GENERATE_PDF = "UNABLE_TO_GENERATE_PDF",
  PDF_ALREADY_EXISTS = "PDF_ALREADY_EXISTS",
  /** CSV file exists and download URL is available */
  CSV_ALREADY_EXISTS = "CSV_ALREADY_EXISTS",
  /** CSV not yet generated or unavailable for download */
  UNABLE_TO_GENERATE_CSV = "UNABLE_TO_GENERATE_CSV",
  /**
   * Generation has been requested; CSV will be available after the job runs (same pipeline as PDF).
   */
  REQUEST_RECEIVED_CSV_GENERATION_IN_PROGRESS = "REQUEST_RECEIVED_CSV_GENERATION_IN_PROGRESS",
}

interface DownloadOrgBillRespDTO{
  fileDownloadURL?:string;
  fileName?:string;
  status?:DownloadOrgBillAgencyResStatus;
}

/**
 * Requested download format for a jobber pay stub.
 */
enum JobberPayStubFileType {
  PDF = "PDF",
  CSV = "CSV"
}

interface DownloadJobberPayStubAgencyReqDTO{
  jobberEmail: EmailAddress;
  payStubCycleStartDateTimeEpochSec: number;
  payStubCycleEndDateTimeEpochSec: number;
  workJurisdictionIdentifier: WorkJurisdictionIdentifier;
  recordRequestType: PDFRecordRequestType;
  fileType: JobberPayStubFileType;
}

enum DownloadJobberPayStubAgencyResStatus {
  /**
   * This means a SQS message has been sent to generate this pdf (and CSV when applicable).
   */
  REQUEST_RECEIVED_PDF_GENERATION_IN_PROGRESS = "REQUEST_RECEIVED_PDF_GENERATION_IN_PROGRESS",
  PDF_ALREADY_EXISTS = "PDF_ALREADY_EXISTS",
  CSV_ALREADY_EXISTS = "CSV_ALREADY_EXISTS"
}

interface DownloadJobberPayStubAgencyResDTO{
  fileDownloadURL?:string;  
  fileName?:string;
  status?:DownloadJobberPayStubAgencyResStatus;
}

interface JobberAvalTimeWindows{
  cities: string[]
  dayIntervals: string[]
  dayOfTheWeek: string[]
}
interface JobberEmploymentDeductionsConfigDTO{
  readonly corpJobberEmploymentId?: string;
  readonly corpJobberEmploymentTypeName?: string;
  readonly jobberWorkerEmploymentType?: JobberWorkerEmploymentType;
  readonly workJurisdictionIdentifier?: WorkJurisdictionIdentifier;
  readonly jobberNote?: string;
}

enum JobberOrgLocationStatus {
  TRAINED = "TRAINED"
}

interface OrgJobberLocationConfigDTO {
  readonly orgName: string;
  readonly locationName: string;
  readonly jobberOrgLocationStatus: JobberOrgLocationStatus;
}

interface WorkerIntegrationConfigDTO {
  readonly integration: 'QBO';
  readonly dataSyncHourStatus?: 'ENABLED' | 'DISABLED' | null;
}

interface JobberFullProfileDetails{
  jobberDetails: WorkyUserDetails;
  jobberStatus?:JobberStatus;
  readonly socialInsuranceNumber?: string | null;
  readonly dateOfBirth?: string | null;
  readonly address?: AddressDto | null;
  jobberAvailability: JobberAvalTimeWindows;
  jobberWorkerTypes:JobberWorkerTypeAgencyPrivateViewDTO[]
  jobberEmploymentDeductionsConfig: JobberEmploymentDeductionsConfigDTO[];
  orgLocationConfigs?: readonly OrgJobberLocationConfigDTO[];
  jobberContractorDetails?: JobberContractorDetailsDTO;
  /** Missing list or missing QBO entry means this worker's hours are sent to QuickBooks. */
  readonly workerIntegrationConfigs?: readonly WorkerIntegrationConfigDTO[] | null;
}

interface ShiftRequestAssignmentReqObjWJobber{
  shiftId: string;
  jobberEmail: EmailAddress,
  isOverrideAndDeleteFlag: boolean
}

enum DirectShiftAssignmentRespStatus {
  SUCCESS = "SUCCESS",
  NOT_ASSIGNED = "NOT_ASSIGNED"
}

enum DirectSAErrorRespType {
  /**
   * NOT RETRYABLE
   */
  JOBBER_NOT_QUALIFIED = "JOBBER_NOT_QUALIFIED",
  /**
   * NOT RETRYABLE
   */
  JOBBER_ALREADY_BOOKED = "JOBBER_ALREADY_BOOKED",
  /**
   * NOT RETRYABLE
   */
  SHIFT_ALREADY_ASSIGNED = "SHIFT_ALREADY_ASSIGNED",
  /**
   * NOT RETRYABLE
   */
  SHIFT_CONFIG_ISSUE = "SHIFT_CONFIG_ISSUE",
  /**
   * RETRYABLE
   */
  TECHNICAL_ERROR = "TECHNICAL_ERROR"
}

interface DirectShiftAssignmentRespReasons {
  // Placeholder for future reason fields
}

interface DirectShiftAssignmentResp {
  readonly shiftId: string;
  readonly jobberEmail: EmailAddress;
  readonly respStatus: DirectShiftAssignmentRespStatus;
  readonly message: string;
  readonly jobberDetails?: WorkyUserDetailsZip | null;
  readonly directSAErrorRespType?: DirectSAErrorRespType | null;
  readonly reasons?: readonly DirectShiftAssignmentRespReasons[];
  readonly shiftStartDateTime: number;
  readonly shiftEndDateTime: number;
}




enum SkillLevel {
    BEGINNER = 'BEGINNER',
    INTERMEDIATE = 'INTERMEDIATE',
    ADVANCED = 'ADVANCED',
    EXPERT = 'EXPERT'
}


interface RegisterJobberRequestSimpleJobberworkerTypeDTO{
  jobberWorkerTypeName: string,
  jobberWorkerTypeNameDefaultPayRate: number
}

enum JobberWorkerEmploymentType {
  FULL_TIME_EMPLOYEE = 'FULL_TIME_EMPLOYEE',
  CONTRACTOR = 'CONTRACTOR'
}

interface RegisterJobberRequestSimpleDTO{
  jobberEmailAddress: string,
  jobberPhoneNumber?: string,
  jobberFirstName?: string,
  jobberLastName?: string,
  jobberWorkerTypes?: RegisterJobberRequestSimpleJobberworkerTypeDTO[],
  corpJobberEmploymentId: string,
  /** Jurisdiction for the worker's paystub / employment deduction config map key. */
  workJurisdictionIdentifier?: string,
  templateId?: string
}

interface UpdateJobberNoteOnEmploymentConfig {
  jobberEmailAddress: EmailAddress;
  corpJobberEmploymentId: string;
  note: string;
}

interface UpdateJobberQuickBooksHoursSyncReqDTO {
  jobberEmailAddress: EmailAddress;
  dataSyncHourStatus: 'ENABLED' | 'DISABLED';
}

interface SkillQualificationDetailsDTO{
  requiredMandatorySkill: boolean,
  skillLevel: SkillLevel,
}
interface CreateJobberWorkerTypeReqDTO{
  workerTypeName: string,
  defaultJobberPayRate: number,
  orgHourlyRateMarkupType: OrgHourlyRateMarkupType,
  defaultOrgBillingPayRate: number,
  orgMarkUpValue?: number,
  jobberSkillQualificationsRequirements?: Record<string, SkillQualificationDetailsDTO>
  description?: string
  /**
   * Optional sales-tax rules for Ontario, British Columbia, and Alberta.
   * Saved with the worker type so tax is set in the same create.
   */
  laborSalesTaxRules?: LaborSalesTaxRuleDTO[] | null
}


interface CommunicationPreferencesV2{
  defaultEmailAddress: string;
  isEmailVerified?: boolean;
  defaultPhoneNumber: string;
  preferredCommunicationMethod?: PreferredCommunicationMethod;
}
interface CommunicationPreferences {
  defaultEmailAddress: EmailAddress;
  isEmailVerified?: boolean;
  defaultPhoneNumber: PhoneNumber;
  preferredCommunicationMethod?: PreferredCommunicationMethod;
}


interface CorpCustomerShiftDetails {
  isLunchBreakCovered: boolean;
  workWeekStartDay: string; // DayOfWeek enum value 
  workWeekEndDay: string;   // DayOfWeek enum value
}

interface CreateJobberWorkerTypeDTO {
  workerTypeName?: string;
  workerTypeUniqueIdentifier: string;
  defaultJobberPayRate: number;
  orgHourlyRateMarkupType: OrgHourlyRateMarkupType;
  orgHourlyRateMarkupTypeValue: number;
  orgDefaultJobberPayRateForPayroll?: number | null;
}

interface CreateAgencyOrgJobberWorkerTypeDetailsDTO {
  createJobberWorkerTypeDTO: CreateJobberWorkerTypeDTO;
  /** Optional Layer B labor sales-tax rules for this client × worker type (ON/BC/AB). */
  readonly laborSalesTaxRules?: LaborSalesTaxRuleDTO[] | null;
}

interface OrgLocationClient {
  orgName?: string;
  locationName?: string;
  /**
   * Resolved UI label for this facility (server: OrgLocation#getLocationDisplayName()).
   * Always populated in API responses; for local drafts use the facility key or empty until set.
   */
  locationDisplayName: string;
  /**
   * Visibility for new work. DISABLED hides the site from pickers. Omitted or null is ACTIVE.
   */
  orgLocationDisplayStatus?: OrgLocationDisplayStatus;
  locationAddress?: AddressDto;
  servicedCity?: ServicedCity;
  /**
   * Work jurisdiction for this facility. Defaults to ONTARIO when absent.
   * Drives the default shift wall-clock timezone (BRITISH_COLUMBIA -> America/Vancouver;
   * ALBERTA -> America/Edmonton).
   */
  workJurisdictionIdentifier?: WorkJurisdictionIdentifier;
  primaryShiftManagerContact?: UserDetails;
  primaryHRManagerContact?: UserDetails; 
  orgUserContactsForLocation?: UserDetails[];
  orgJobberMatchingType?: OrgJobberMatchingType;
  orgClientHourlyDeductionConfig?: OrgClientHourlyDeductionConfigDTO;
}

interface CreateOrgClientHourlyDeductionConfigDetailDTO {
  detailName: string;
  minShiftLengthForRuleToApplyMinutes?: number;
  maxShiftLengthForRuleToApplyMinutes?: number;
  shiftTimeWorkedDeductionAmountMinutes: number;
  orgClientHourlyDeductionApplicTypes?: OrgClientHourlyDeductionApplicType[];
}

interface CreateOrgClientHourlyDeductionConfigDTO {
  deductionConfigDetails: readonly CreateOrgClientHourlyDeductionConfigDetailDTO[];
}

enum OrgClientHourlyDeductionType {
  SHIFT_BREAK_DEDUCTION = "SHIFT_BREAK_DEDUCTION"
}

interface OrgClientHourlyDeductionConfigDetailDTO {
  readonly detailId: string;
  readonly detailName?: string;
  readonly orgClientHourlyDeductionType: OrgClientHourlyDeductionType;
  readonly minShiftLengthForRuleToApplySeconds: number;
  readonly maxShiftLengthForRuleToApplySeconds: number;
  readonly shiftTimeWorkedDeductionAmountSeconds: number;
  readonly orgClientHourlyDeductionApplicTypes?: OrgClientHourlyDeductionApplicType[];
}

interface OrgClientHourlyDeductionConfigDTO {
  readonly orgClientHourlyDeductions: readonly OrgClientHourlyDeductionConfigDetailDTO[];
}

interface CreateOrgLocationClient {
  orgName?: string;
  locationName?: string;
  locationAddress?: AddressDto;
  servicedCity?: ServicedCity;
  /**
   * Work jurisdiction for the new facility. Defaults to ONTARIO on the server when omitted.
   * Set to BRITISH_COLUMBIA or ALBERTA for those client locations.
   */
  workJurisdictionIdentifier?: WorkJurisdictionIdentifier;
  primaryShiftManagerContact?: UserDetails;
  primaryHRManagerContact?: UserDetails; 
  orgUserContactsForLocation?: UserDetails[];
  orgJobberMatchingType?: OrgJobberMatchingType;
  createOrgClientHourlyDeductionConfig?: CreateOrgClientHourlyDeductionConfigDTO;
}


interface OrgDetailsCreateReqDTO {
  orgLocationList: CreateOrgLocationClient[];
  billingTimeInterval: BillingTimeInterval;
  anchor?: number;
  /** Tax authority for this org's client billing. */
  workJurisdictionIdentifier?: WorkJurisdictionIdentifier;
  agencyOrgJobberWorkerTypeDetailsDTOMap?: CreateAgencyOrgJobberWorkerTypeDetailsDTO[];
}


interface CreateOrgToAgencyObjReqDTO {
  orgName: string;
  addressDto: AddressDto;
  corpCustomerShiftDetails: CorpCustomerShiftDetails;
  communicationPreferences: CommunicationPreferencesV2;
  orgDetailsCreateReqDTO: OrgDetailsCreateReqDTO;
  wsipBucketName?: string;
}


interface CreateOrgForAgencyFormData {
  orgName: string;
  locationAddress: AddressDto;
  managerName: string;
  managerEmail: string;
  managerPhoneNum: string;  
  orgLocationClient: CreateOrgLocationClient[]
  billingInterval: BillingTimeInterval;
  billingDay: string;
  billingCycleAnchorDate: string;
  workerType?: JobberWorkerTypeAgencyPrivateViewDTO[];
}

interface HireJobberAgencyReq {
  jobberEmail: EmailAddress
}


interface AddJobberWorkerTypeReqDTO {
  jobberEmailAddress: EmailAddress;
  workerTypeName: string;
  workerTypeUniqueIdentifier: string;
  defaultJobberPayRate: number;
}

interface UpdateJobberWorkerTypePayRateReqDTO {
  readonly jobberEmailAddress: EmailAddress;
  readonly workerTypeUniqueIdentifier: string;
  readonly jobberHourlyPayRate: number;
}

interface ModifyJobberWorkerHourlyRateReqDTO {
  readonly shiftId: string;
  readonly jobberWorkerHourlyRate: number;
}

interface ModifyOrgBillingRateReqDTO {
  readonly shiftId: string;
  readonly orgBillingRate: number;
}

interface ModifyShiftActualTimesReqDTO {
  readonly shiftId: string;
  readonly actualStartDateTimeEpochSeconds: number | null;
  readonly actualEndDateTimeEpochSeconds: number | null;
}

interface ModifyShiftClockTimesReqDTO {
  readonly shiftId: string;
  readonly actualStartDateTimeEpochSeconds: number | null;
  readonly actualEndDateTimeEpochSeconds: number | null;
}

interface CancelShiftPaymentWorkerPayReqDTO {
  readonly shiftId: string;
  readonly jobberEmail: EmailAddress;
}

interface CancelShiftPaymentOrgBillingReqDTO {
  readonly shiftId: string;
  readonly orgName: string;
}

interface AddFacilityToOrgRequestDTO {
  orgName: string;
  orgLocationList: OrgLocationClient[];
}

interface AddWorkerTypeToOrgRequestDTO {
  orgName: string;
  agencyOrgJobberWorkerTypeDetails: CreateAgencyOrgJobberWorkerTypeDetailsDTO;
}

interface RemoveWorkerTypeFromOrgRequestDTO {
  readonly orgName: string;
  readonly workerTypeUniqueIdentifier: string;
}

interface UpdateOrgWorkerTypePayRateRequestDTO {
  orgName: string;
  createJobberWorkerTypeDTO: CreateJobberWorkerTypeDTO;
  /**
   * When set, upserts Layer B labor sales-tax rules with pay rates.
   * When omitted/null on update, existing Layer B rules are preserved.
   */
  readonly laborSalesTaxRules?: LaborSalesTaxRuleDTO[] | null;
}

interface UpdateDefaultWorkerTypePayRateRequestDTO {
  workerTypeUniqueIdentifier: string;
  createJobberWorkerTypeDTO: CreateJobberWorkerTypeDTO;
}

interface UpdateJobberWorkerTypeLaborSalesTaxRequestDTO {
  readonly workerTypeUniqueIdentifier: string;
  readonly laborSalesTaxRules?: LaborSalesTaxRuleDTO[] | null;
}

interface UpdateOrgWorkerTypeLaborSalesTaxRequestDTO {
  readonly orgName: string;
  readonly workerTypeUniqueIdentifier: string;
  readonly laborSalesTaxRules?: LaborSalesTaxRuleDTO[] | null;
}

/**
 * Request DTO for updating the org billing worker type name.
 * orgBillingWorkerTypeName: new name for org billing; when set, used for invoice generation for the org instead of workerTypeName.
 * Null or empty clears the override (workerTypeName will be used).
 */
interface UpdateOrgBillingWorkerTypeNameRequestDTO {
  readonly workerTypeUniqueIdentifier: string;
  readonly orgBillingWorkerTypeName?: string | null;
}

interface ChangePayCycleStatusReqDTO {
  readonly payStubCycleStartDateTimeEpochSec: number;
  readonly payStubCycleEndDateTimeEpochSec: number;
  readonly workJurisdictionIdentifier?: WorkJurisdictionIdentifier;
  readonly status: WorkyPayPayCycleStatus;
}

interface GetPayrollCycleReqDTO {
  readonly payStubCycleStartDateTimeEpochSec: number;
  readonly payStubCycleEndDateTimeEpochSec: number;
  readonly workJurisdictionIdentifier?: WorkJurisdictionIdentifier;
}

enum ShiftPaymentStatus {
  CREATED = "CREATED",
  JOBBER_ASSIGNED = "JOBBER_ASSIGNED",
  JOBBER_CLOCKED_IN = "JOBBER_CLOCKED_IN",
  JOBBER_CLOCKED_OUT = "JOBBER_CLOCKED_OUT",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED"
}

/**
 * The ShiftPayment has just been created
 * from a completed ShiftAssignment
 */
enum ShiftPaymentJobberStatus {
  UNPAID_CREATED = "UNPAID_CREATED",

  /**
   * The shift payment for the jobber has been created
   * & Worky staff has been notified
   */
  UNPAID_JOBBER_PAYMENT_CREATED = "UNPAID_JOBBER_PAYMENT_CREATED",

  /**
   * The jobber has submitted a dispute of the hours for the shift,
   * typically requires review by agency admin
   */
  JOBBER_HOURLY_DISPUTE = "JOBBER_HOURLY_DISPUTE",

  /**
   * The shift payment for the jobber has
   * been directly approved by Worky Staff
   * and direct deposit has been sent
   */
  PAID_JOBBER_PAYSTUB_FULLY_COMPLETE = "PAID_JOBBER_PAYSTUB_FULLY_COMPLETE"
}

enum ShiftPaymentOrgStatus {
  UNPAID_PAY_CYCLE_IN_PROGRESS = "UNPAID_PAY_CYCLE_IN_PROGRESS",
  UNPAID_CREATED = "UNPAID_CREATED",
  UNPAID_ORG_PAYMENT_CREATED = "UNPAID_ORG_PAYMENT_CREATED",
  UNPAID_ORG_PAYMENT_APPROVED_AND_ORG_NOTIFIED = "UNPAID_ORG_PAYMENT_APPROVED_AND_ORG_NOTIFIED",
  UNPAID_ORG_PAYMENT_DELAYED = "UNPAID_ORG_PAYMENT_DELAYED",
  PAID_ORG_PAYMENT_RECEIVED_FULLY_COMPLETE = "PAID_ORG_PAYMENT_RECEIVED_FULLY_COMPLETE"
}

interface PaymentDetailsDTO {
  paymentDueDateEpochSec: number;
  paymentDueDate: string; 
}

interface OrgLocationSimpleClient {
  simpleLocation: ServicedCity;
  locationDisplayName: string;
  locationName: string;
  locationAddress: AddressDto;
  /**
   * Work jurisdiction for this facility. Used to derive shift wall-clock timezone
   * when pay-cycle jurisdiction is not provided.
   */
  workJurisdictionIdentifier?: WorkJurisdictionIdentifier;
}
interface ShiftWorkDetailsDTO{
  startDateTimeEpochSeconds: number;
  endDateTimeEpochSeconds: number;
  jobberWorkerType: JobberWorkerTypeDTO;
  actualStartDateTimeEpochSeconds: number;
	actualStartDateTime: string;
	actualEndDateTimeEpochSeconds: number;
	actualEndDateTime: string;
}


interface ShiftPaymentDTO {
  shiftId: string;
  parentShiftRequestId: string;
  originalBillAmount: number;
  currentBillAmount: number;
  workyShiftId?: string | null;
  orgName: string;
  orgDisplayName:string;
  jobberUserDetails: WorkyUserDetails;
  requestingUserDetails: WorkyUserDetails;
  paymentDetails: PaymentDetailsDTO;
  orgLocationSimpleClient: OrgLocationSimpleClient;
  shiftStatus: ShiftPaymentStatus;
  workyBillPaymentBreakdown: WorkyBillPaymentBreakdownDTO;
  jobberShiftPaymentStatus?: ShiftPaymentJobberStatus | null;
  orgShiftPaymentStatus?: ShiftPaymentOrgStatus | null;
  assignedJobberProfilePicS3Key?: string | null;
  shiftWorkDetails: ShiftWorkDetailsDTO;  
	checkInDateTimeEpoch: number;
	checkInDateTime: string;
	checkOutDateTimeEpoch: number;
	checkOutDateTime: string;
  shiftPaymentJobberDisputeRequest?: readonly ShiftPaymentJobberDisputeDTO[] | null;
  shiftClockDetails?: AgencyAdminShiftClockDetailsDTO;
  orgShiftBillingDetailsData?: AgencyOrgBillingShiftBillingDetailsDataDTO | null;
  /**
   * Agency–worker pay stub details for this shift (aligned with server ShiftDetailsJobberPayStubView).
   * Set when the shift appears on a finalized jobber pay stub for the original shift payment.
   */
  workerPayStubShiftDetails?: ShiftDetailsJobberPayStubDTO | null;
  /** When set, overrides base actual start for client/org billing only. */
  orgBillingStartDateTimeEpochSecondsOverride?: number | null;
  /** When set, overrides base actual end for client/org billing only. */
  orgBillingEndDateTimeEpochSecondsOverride?: number | null;
  /** When set, overrides base actual start for worker payroll / jobber paystub only. */
  payrollStartDateTimeEpochSecondsOverride?: number | null;
  /** When set, overrides base actual end for worker payroll / jobber paystub only. */
  payrollEndDateTimeEpochSecondsOverride?: number | null;
}

enum DeductionType {
  PERCENTAGE = "PERCENTAGE",
  FLAT_AMOUNT = "FLAT_AMOUNT"
}

interface CreateWorkyPayCustomDeductionDetailDTO {
  readonly deductionName: string;
  readonly deductionAmount: number;
  readonly deductionType: DeductionType;
}

/**
 * This is the config for determining the logic
 * for stat holiday premium pay
 */
interface CreateWorkyPayPremiumPayHolidayPayRule {
  /**
   * Try to give this as a percent, e.g 1.5 or 0.5
   */
  readonly premiumMultiplier: number;
  readonly ruleName: string;
  readonly premiumPayApplicConfigTypes?: PremiumPayApplicConfigType[];
}

interface CreateCorpJobberEmploymentDeductionReqDTO {
  readonly workJurisdictionIdentifier: string;
  readonly corpJobberEmploymentTypeName: string;
  readonly jobberWorkerEmploymentType: JobberWorkerEmploymentType;
  readonly deductionDetails: readonly CreateWorkyPayCustomDeductionDetailDTO[] | null;
  readonly corpObligations: readonly CreateWorkyPayCustomDeductionDetailDTO[] | null;
  /**
   * This is the config for determining the logic
   * for stat holiday premium pay
   */
  readonly workyPayPremiumPayHolidayPayRuleConfig?: CreateWorkyPayPremiumPayHolidayPayRule;
  readonly workerPayShiftBreakDeductionMode?: WorkerPayShiftBreakDeductionMode | null;
}

interface UpdateWorkerPayShiftBreakDeductionModeReqDTO {
  readonly corpJobberEmploymentId: string;
  readonly workerPayShiftBreakDeductionMode?: WorkerPayShiftBreakDeductionMode | null;
}

interface AddHolidayPremiumPayRuleReqDTO {
  readonly corpJobberEmploymentId: string;
  readonly workyPayPremiumPayHolidayPayRuleConfig: CreateWorkyPayPremiumPayHolidayPayRule;
  readonly premiumPayApplicConfigTypes?: PremiumPayApplicConfigType[];
}

/**
 * Appends custom agency premium-pay dates (ISO-8601 yyyy-MM-dd) to an existing
 * holiday premium pay rule on a corp jobber employment deduction config.
 */
interface AddAgencyPremiumPayHolidayDatesReqDTO {
  readonly corpJobberEmploymentId: string;
  /** Matches WorkyPayPremiumPayHolidayPayRule.ruleId on the server. */
  readonly ruleId: string;
  readonly premiumDateConfigLabDisplayNote?: string;
  readonly premiumPayDates: readonly string[];
}

interface UpdateAgencyOrgBillStatusReqDTO {
  orgName: string;
  billingCycleStartEpoch: number;
  billingCycleEndEpoch: number;
  newStatus: ShiftPaymentOrgStatus;
}

interface MapJobbersToOrgLocationRequestDTO {
  readonly orgName: string;
  readonly orgLocationName: string;
  readonly jobberEmails: readonly EmailAddress[];
}

interface UpdateOrgLocationJobberMatchingTypeRequestDTO {
  readonly orgName: string;
  readonly orgLocationName: string;
  readonly orgJobberMatchingType: OrgJobberMatchingType | null;
}

interface AddOrgLocationHourlyDeductionConfigRequestDTO {
  readonly orgName: string;
  readonly orgLocationName: string;
  readonly orgClientHourlyDeductionConfig: CreateOrgClientHourlyDeductionConfigDTO;
}

interface DeleteOrgLocationHourlyDeductionConfigRequestDTO {
  readonly orgName: string;
  readonly orgLocationName: string;
  readonly detailId: string;
}

/**
 * Request DTO for updating an org's display name.
 * newDisplayName: null or empty clears the override (org name will be used).
 */
interface UpdateOrgDisplayNameRequestDTO {
  readonly orgNameKey: string;
  readonly newDisplayName: string | null;
}

/**
 * POST agency/org-client-config/update-org-details-agency-display-status
 */
interface UpdateOrgDetailsAgencyDisplayStatusRequestDTO {
  readonly orgName: string;
  readonly orgDetailsAgencyDisplayStatus: OrgDetailsAgencyDisplayStatus;
}

interface OnboardingQuestionResponseDTO {
  questionId: string;
  question: string;
  questionType: FormSubElementTypes;
  required: boolean;
  description?: string;
}

interface FormSectionResponseDTO {
  sectionId: string;
  sectionTitle: string;
  sectionDescription?: string | null;
  questions: OnboardingQuestionResponseDTO[];
  isDefaultSection: boolean;
}

interface JobberOnboardingTemplateResponseDTO {
  org: string;
  templateName: string;
  templateId: string;
  templateCreatorId: string;
  templateStatus?: TemplateStatusEnum;
  sections?: FormSectionResponseDTO[];
  onboardingQuestions?: OnboardingQuestionResponseDTO[];
  metaData: WorkyObjectMetaData;
}

interface WorkyObjectMetaData {
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
  version: number;
  isActive: boolean;
}

interface SectionConditionRequestDTO {
  targetSectionName: string;
  operator: ConditionOperator;
  expectedValues: readonly string[];
}

interface OnboardingQuestionRequestDTO {
  question: string;
  questionType: OnboardingQuestionType;
  required: boolean;
  description?: string | null;
  options?: readonly string[] | null;
  showSectionsWhen?: readonly SectionConditionRequestDTO[] | null;
  staticText?: string | null;
}

interface FormSectionRequestDTO {
  sectionTitle: string;
  sectionDescription?: string | null;
  questions: readonly OnboardingQuestionRequestDTO[];
  isDefaultSection: boolean;
}

interface CreateOnboardingTemplateRequestDTO {
  templateName: string;
  sections: readonly FormSectionRequestDTO[];
}

interface QuestionTypeOption {
  value: string;
  label: string;
}

const QUESTION_TYPES: readonly QuestionTypeOption[] = [
  { value: OnboardingQuestionType.TEXT_FIELD, label: 'Text Input' },
  { value: OnboardingQuestionType.TIME_HH_MM, label: 'Time (HH:mm, 24-hour)' },
  { value: OnboardingQuestionType.DATE_YYYY_MM_DD, label: 'Date (YYYY-MM-DD)' },
  { value: OnboardingQuestionType.CANADIAN_POSTAL_CODE, label: 'Canadian Postal Code' },
  { value: OnboardingQuestionType.NUMBER, label: 'Number (digits only)' },
  { value: OnboardingQuestionType.FILE_TYPE, label: 'File Upload' },
  { value: OnboardingQuestionType.FILE_TYPE_WITH_EXPIRY, label: 'File Upload with Expiry Date' },
  { value: OnboardingQuestionType.DROP_DOWN_SELECT, label: 'Dropdown Select' },
  { value: OnboardingQuestionType.STATIC_TEXT, label: 'Static text (display only)' }
] as const;

interface AssignJobberToShiftRequestDTO {
  shiftId: string;
  jobberResponseId: string;
}

enum ShiftTemplateStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

interface ShiftTemplateDTO {
  readonly templateName: string;
  readonly templateId: string;
  readonly orgName: string;
  readonly orgDisplayName: string;
  readonly facilityName: string;
  readonly status?: ShiftTemplateStatus;
  /**
   * 24 hour format
   */
  readonly startHour: number;
  readonly startMinute: number;
  /**
   * 24 hour format
   */
  readonly endHour: number;
  readonly endMinute: number;
  readonly ruleId?: string | null;
  readonly numberOfWorkers: number;
  readonly jobberWorkerTypeId: string;
}

interface UpdateShiftTemplateStatusRequestDTO {
  readonly templateId: string;
  readonly status: ShiftTemplateStatus;
}

interface UpdateShiftTemplateNameRequestDTO {
  readonly templateId: string;
  readonly templateName: string;
}

interface CreateShiftTemplateRequestDTO {
  readonly templateName: string;
  readonly orgName: string;
  readonly facilityName: string;
  /**
   * 24 hour format
   */
  readonly startHour: number;
  /**
   * 24 hour format
   */
  readonly endHour: number;
  /**
   * 0-59
   */
  readonly endMinute: number;
  /**
   * 0-59
   */
  readonly startMinute: number;
  readonly ruleId?: string | null;
  readonly numberOfWorkers: number;
  readonly jobberWorkerTypeId: string;
}


interface ResendJobberRegistrationEmailRequestDTO {
  readonly jobberEmail: EmailAddress;
}


interface ChangeJobberStatusReqDTO {
  jobberEmails: EmailAddress[];
  newJobberStatus: JobberStatus;
}

interface UpdateCorpOwnerReviewReqDTO {
  readonly workJurisdictionIdentifier: WorkJurisdictionIdentifier;
  readonly payStubCycleStartDateTimeEpochSec: number;
  readonly payStubCycleEndDateTimeEpochSec: number;
  readonly jobberEmail: EmailAddress;
  readonly status: PayCorpOwningReviewStatus;
  readonly corpOwnerNote?: string | null;
}

interface WorkyPayCorpOwnerReviewDetail {  
  readonly reviewRecordId: string;
  readonly corpOwnerUser: WorkyUserDetails;
  readonly recordCreatedDateTimeEpochSec: number;
  readonly corpOwningReviewStatus: PayCorpOwningReviewStatus;
  readonly corpOwnerNote?: string | null;
}

interface UpdateCorpOwnerReviewResDTO {
  readonly workyPayCorpOwnerReview: WorkyPayCorpOwnerReviewDetail;
}


export {
  ResendJobberRegistrationEmailRequestDTO,
  AssignJobberToShiftRequestDTO,
  AddJobberWorkerTypeReqDTO,
  UpdateJobberWorkerTypePayRateReqDTO,
  ModifyJobberWorkerHourlyRateReqDTO,
  ModifyOrgBillingRateReqDTO,
  ModifyShiftActualTimesReqDTO,
  ModifyShiftClockTimesReqDTO,
  CancelShiftPaymentWorkerPayReqDTO,
  CancelShiftPaymentOrgBillingReqDTO,
  HireJobberAgencyReq,
  CreateJobberWorkerTypeReqDTO,
  PreferredCommunicationMethod,
  RegisterJobberRequestSimpleDTO,
  RegisterJobberRequestSimpleJobberworkerTypeDTO,
  UpdateJobberNoteOnEmploymentConfig,
  UpdateJobberQuickBooksHoursSyncReqDTO,
  JobberWorkerEmploymentType,
  AgencyUser,
  AgencyObjClient,
  CorpConfigBasicDetailsClient,
  CurrentAgencyPayStubsRespDTO,
  CurrentAgencyPayStubsJobberLevelDTO,
  ShiftDetailsJobberPayStubDTO,
  JobberHourlyRateToBeUsedForPayrollCalcDTO,
  JobberHourlyRateToBeUsedForPayrollCalcDetailDTO,
  WorkyBillPaymentBreakdownDTO,
  ShiftBillingDetailsDataDTO,  
  ShiftBillingDetailsDTO,
  AgencyOrgBillingParallelInvoiceFileDetailDTO,
  AgencyOrgBillingShiftBillingDetailsDataDTO,
  AgencyOrgBillingCycleSummaryDTO,
  ListAgencyOrgBillingCycleSummaryHeadersRespDTO,
  SearchAgencyOrgBillsByInvoiceNumberRespDTO,
  JobberHourlyRateToBeUsedForReceivingCorpBillingDTO,
  JobberHourlyRateToBeUsedForReceivingCorpBillingDetailDTO,
  WorkyBillBreakDownAgencyOrgBillingSummaryView,
  TimeClockDataRespDTO,
  TimeClockOrgDataRespDTO,
  TimeClockByOrgByDateByLocationDTO,
  JobberCheckInOutDataDTO,
  CheckInOutDataDTO,
  CheckInOutType,
  DownloadOrgBillReqDTO,
  AgencyOrgBillRowObj,
  DownloadOrgBillRespDTO,
  DownloadOrgBillAgencyResStatus,
  JobberPayStubFileType,
  DownloadJobberPayStubAgencyReqDTO,
  DownloadJobberPayStubAgencyResDTO,
  DownloadJobberPayStubAgencyResStatus,
  JobberFullProfileDetails,
  WorkerIntegrationConfigDTO,
  JobberAvalTimeWindows,
  ShiftRequestAssignmentReqObjWJobber,
  DirectShiftAssignmentResp,
  DirectShiftAssignmentRespStatus,
  DirectSAErrorRespType,
  DirectShiftAssignmentRespReasons,
  ShiftAssignmentConfigRuleType,
  ShiftAssignmentConfigRuleApplicationType,
  SkillQualificationDetailsDTO,
  OrgHourlyRateMarkupType,
  SkillLevel,
  CreateOrgToAgencyObjReqDTO,
  OrgJobberMatchingType,
  CreateOrgForAgencyFormData,
  OrgLocationClient,
  AddFacilityToOrgRequestDTO,
  OrgDetailsCreateReqDTO,
  AddWorkerTypeToOrgRequestDTO,
  RemoveWorkerTypeFromOrgRequestDTO,
  CreateAgencyOrgJobberWorkerTypeDetailsDTO,
  CreateJobberWorkerTypeDTO,
  UpdateOrgWorkerTypePayRateRequestDTO,
  UpdateDefaultWorkerTypePayRateRequestDTO,
  UpdateJobberWorkerTypeLaborSalesTaxRequestDTO,
  UpdateOrgWorkerTypeLaborSalesTaxRequestDTO,
  UpdateOrgBillingWorkerTypeNameRequestDTO,
  ChangePayCycleStatusReqDTO,
  GetPayrollCycleReqDTO,
  WorkyPayPayCycleStatus,
  ShiftPaymentDTO,
  ShiftPaymentStatus,
  ShiftPaymentJobberStatus,
  ShiftPaymentOrgStatus,
  PaymentDetailsDTO,
  OrgLocationSimpleClient,
  ShiftWorkDetailsDTO,
  DeductionType,
  CreateWorkyPayCustomDeductionDetailDTO,
  CreateWorkyPayPremiumPayHolidayPayRule,
  CreateCorpJobberEmploymentDeductionReqDTO,
  UpdateWorkerPayShiftBreakDeductionModeReqDTO,
  AddHolidayPremiumPayRuleReqDTO,
  AddAgencyPremiumPayHolidayDatesReqDTO,
  UpdateAgencyOrgBillStatusReqDTO,
  JobberEmploymentDeductionsConfigDTO,
  OnboardingQuestionResponseDTO,
  JobberOnboardingTemplateResponseDTO,
  FormSectionResponseDTO,
  WorkyObjectMetaData,
  CreateOnboardingTemplateRequestDTO,
  TemplateStatusEnum,
  QUESTION_TYPES,
  QuestionTypeOption,
  OnboardingQuestionRequestDTO,
  FormSectionRequestDTO,
  SectionConditionRequestDTO,
  MapJobbersToOrgLocationRequestDTO,
  UpdateOrgLocationJobberMatchingTypeRequestDTO,
  JobberOrgLocationStatus,
  OrgJobberLocationConfigDTO,
  PreloadJobber,
  CommunicationPreferencesV2,
  ChangeJobberStatusReqDTO,
  ShiftTemplateStatus,
  ShiftTemplateDTO,
  UpdateShiftTemplateStatusRequestDTO,
  UpdateShiftTemplateNameRequestDTO,
  CreateShiftTemplateRequestDTO,
  PDFRecordRequestType,
  CreateOrgClientHourlyDeductionConfigDetailDTO,
  CreateOrgClientHourlyDeductionConfigDTO,
  CreateOrgLocationClient,
  OrgClientHourlyDeductionType,
  OrgClientHourlyDeductionConfigDetailDTO,
  OrgClientHourlyDeductionConfigDTO,
  AddOrgLocationHourlyDeductionConfigRequestDTO,
  DeleteOrgLocationHourlyDeductionConfigRequestDTO,
  UpdateOrgDisplayNameRequestDTO,
  UpdateOrgDetailsAgencyDisplayStatusRequestDTO,
  PayCorpOwningReviewStatus,
  UpdateCorpOwnerReviewReqDTO,
  WorkyPayCorpOwnerReviewDetail,
  WorkyPayCorpOwnerReview,
  UpdateCorpOwnerReviewResDTO,
  WorkyPayStubRecordSourceType,
  CorpCustomerShiftDetails,
};

